import { AlertTriangle } from "lucide-react";
import type { ResourceDemandRow, ResourceMonthTotal } from "@/hooks/use-hours-reports";
import { cn } from "@/lib/utils";
import { formatDateMonth, formatHrs, formatMonthShort, hrs, pct } from "./report-format";

function RoleCell({ row }: { row: ResourceDemandRow }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {row.demandRoleName ?? "—"}
      {row.roleDiffersFromHome && (
        <span
          className="rounded-full bg-info-100 px-1.5 py-px text-xs font-medium text-info-800"
          title="The Role on this Demand is not the Resource's home Role in NOS."
        >
          Role differs
        </span>
      )}
    </span>
  );
}

function DemandCell({ row }: { row: ResourceDemandRow }) {
  return (
    <>
      <div className="font-medium text-foreground">{row.demandName}</div>
      <div className="text-xs text-muted-foreground">
        {[row.accountName, row.demandType, row.isInternal ? "Internal" : null]
          .filter(Boolean)
          .join(" · ")}
      </div>
    </>
  );
}

/** Every Active Demand the Resource carries in the range (spec S3.1.1). */
export function ResourceDemandsTable({ demands }: { demands: ResourceDemandRow[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className="w-full min-w-[760px] text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left">
            <th className="px-3 py-2 font-medium">Demand</th>
            <th className="px-3 py-2 font-medium">Role on Demand</th>
            <th className="px-3 py-2 font-medium">From</th>
            <th className="px-3 py-2 font-medium">End date</th>
            <th className="px-3 py-2 text-right font-medium">Guidance in range</th>
            <th className="px-3 py-2 text-right font-medium">Actuals to date</th>
          </tr>
        </thead>
        <tbody>
          {demands.map((d) => (
            <tr key={d.key} className="border-b border-border/60 last:border-b-0">
              <td className="px-3 py-2">
                <DemandCell row={d} />
              </td>
              <td className="px-3 py-2">
                <RoleCell row={d} />
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                {d.firstMonth ? formatMonthShort(d.firstMonth) : "—"}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">{formatDateMonth(d.endDate)}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">
                {hrs(d.guidanceHoursInRange)}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">
                {hrs(d.actualHoursToDate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One month, line by line (spec S3.3.1): Guidance, Actuals, Delta and Guidance % per
 * Demand. Totals divide the sums, never average the percentages. A forecast month has no
 * actuals, so it shows Capacity against Guidance instead.
 */
export function ResourceMonthDetail({
  month,
  total,
  demands,
}: {
  month: string;
  total: ResourceMonthTotal;
  demands: ResourceDemandRow[];
}) {
  const index = demands[0]?.months.findIndex((m) => m.month === month) ?? -1;
  const rows = demands
    .map((d) => ({ demand: d, cell: d.months[index] }))
    .filter(
      (r): r is { demand: ResourceDemandRow; cell: NonNullable<typeof r.cell> } =>
        r.cell !== undefined && (r.cell.guidanceHours !== null || r.cell.actualHours !== null),
    );
  const forecast = total.basis === "forecast";
  const sumGuidance = rows.reduce((s, r) => s + (r.cell.guidanceHours ?? 0), 0);
  const sumActual = rows.reduce((s, r) => s + (r.cell.actualHours ?? 0), 0);

  return (
    <section className="flex flex-col gap-3" aria-label={`${formatMonthShort(month)} detail`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-lg font-semibold text-heading">
          {formatMonthShort(month)}
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            {forecast
              ? "Forecast: Guidance vs Capacity"
              : total.basis === "inProgress"
                ? "Month in progress — actuals to date"
                : "Guidance vs Actuals"}
          </span>
        </h2>
        <p className="text-sm text-muted-foreground">
          Capacity {hrs(total.capacityHours)} ·{" "}
          {total.overallocatedHours > 0 ? (
            <span className="font-medium text-error-700">
              <AlertTriangle className="mr-0.5 inline h-3.5 w-3.5" aria-hidden />
              {hrs(total.overallocatedHours)} over
            </span>
          ) : (
            <span>{hrs(total.availableHours)} available</span>
          )}
          {total.dataIssue && (
            <span className="ml-2 font-medium text-warning-800">
              Data issue: Guidance on zero capacity
            </span>
          )}
        </p>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No Guidance or logged hours on an Active Demand this month.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left">
                <th className="px-3 py-2 font-medium">Demand</th>
                <th className="px-3 py-2 font-medium">Role on Demand</th>
                <th className="px-3 py-2 font-medium">End date</th>
                <th className="px-3 py-2 text-right font-medium">Guidance</th>
                <th className="px-3 py-2 text-right font-medium">Actuals</th>
                <th
                  className="px-3 py-2 text-right font-medium"
                  title="Guidance − Actuals. Positive = under-burned."
                >
                  Delta
                </th>
                <th className="px-3 py-2 text-right font-medium" title="Actuals ÷ Guidance">
                  Guidance %
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ demand, cell }) => {
                const g = cell.guidanceHours ?? 0;
                const a = cell.actualHours;
                const noGuidance = !forecast && g <= 0 && (a ?? 0) > 0;
                return (
                  <tr
                    key={demand.key}
                    className={cn(
                      "border-b border-border/60 last:border-b-0",
                      noGuidance && "bg-warning-50",
                    )}
                  >
                    <td className="px-3 py-2">
                      <DemandCell row={demand} />
                    </td>
                    <td className="px-3 py-2">
                      <RoleCell row={demand} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatDateMonth(demand.endDate)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{formatHrs(g)}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">
                      {forecast ? (
                        <span className="text-muted-foreground">Forecast</span>
                      ) : (
                        formatHrs(a ?? 0)
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">
                      {forecast ? "–" : formatHrs(g - (a ?? 0))}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">
                      {forecast ? (
                        "–"
                      ) : noGuidance ? (
                        <span className="inline-flex items-center gap-1 font-sans text-warning-800">
                          <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                          No Guidance
                        </span>
                      ) : (
                        pct(g > 0 ? (a ?? 0) / g : null)
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-border bg-muted/40 font-semibold">
                <td className="px-3 py-2" colSpan={3}>
                  Total
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">
                  {formatHrs(sumGuidance)}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">
                  {forecast ? "–" : formatHrs(sumActual)}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">
                  {forecast ? "–" : formatHrs(sumGuidance - sumActual)}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">
                  {forecast ? "–" : pct(sumGuidance > 0 ? sumActual / sumGuidance : null)}
                  {total.basis === "inProgress" && !forecast ? " MTD" : ""}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
